import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/lib/hooks/use-toast';
import type {
  IntegrationConfig,
  CreateIntegrationPayload,
  IntegrationType,
  IntegrationAuthType,
} from '../types/integration.types';
import { useIntegrationStore } from '../store/integration-store';
import {
  ExtraConfigEditor,
  S4HANA_EXTRA_FIELDS,
  HUBSPOT_EXTRA_FIELDS,
} from './extra-config-editor';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editConfig?: IntegrationConfig | null;
}

const TYPE_OPTIONS = [
  { value: 'SAP_S4HANA', label: 'SAP S/4HANA (Materiales)' },
  { value: 'SAP_C4C', label: 'SAP C4C (CRM / Oportunidades)' },
  { value: 'HUBSPOT', label: 'HubSpot CRM' },
];

const AUTH_OPTIONS = [
  { value: 'BASIC', label: 'Basic (Usuario/Contraseña)' },
  { value: 'OAUTH2', label: 'OAuth 2.0 (Client Credentials)' },
  { value: 'API_KEY', label: 'API Key' },
];

const ODATA_OPTIONS = [
  { value: 'V2', label: 'OData V2' },
  { value: 'V4', label: 'OData V4' },
];

export function IntegrationFormDialog({ open, onOpenChange, editConfig }: Props) {
  const { createConfig, updateConfig } = useIntegrationStore();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState<CreateIntegrationPayload>({
    type: 'SAP_S4HANA',
    name: '',
    baseUrl: '',
    authType: 'BASIC',
    username: '',
    password: '',
    clientId: '',
    clientSecret: '',
    tokenUrl: '',
    apiKey: '',
    syncIntervalMinutes: 60,
    serviceUrl: '',
    odataVersion: 'V2',
    extraConfig: {},
  });

  useEffect(() => {
    if (editConfig) {
      setForm({
        type: editConfig.type,
        name: editConfig.name,
        baseUrl: editConfig.baseUrl,
        authType: editConfig.authType,
        username: editConfig.username || '',
        password: '',
        clientId: editConfig.clientId || '',
        clientSecret: '',
        tokenUrl: editConfig.tokenUrl || '',
        apiKey: '',
        syncIntervalMinutes: editConfig.syncIntervalMinutes,
        serviceUrl: editConfig.serviceUrl || '',
        odataVersion: editConfig.odataVersion || 'V2',
        extraConfig: editConfig.extraConfig ?? {},
      });
    } else {
      setForm({
        type: 'SAP_S4HANA',
        name: '',
        baseUrl: '',
        authType: 'BASIC',
        username: '',
        password: '',
        syncIntervalMinutes: 60,
        odataVersion: 'V2',
        extraConfig: {},
      });
    }
  }, [editConfig, open]);

  // Reset type-specific defaults when the user switches type on a new integration
  const handleTypeChange = (v: string) => {
    if (editConfig) {
      setForm({ ...form, type: v as IntegrationType });
      return;
    }
    if (v === 'HUBSPOT') {
      setForm({
        type: 'HUBSPOT',
        name: form.name,
        baseUrl: '',
        authType: 'API_KEY',
        username: '',
        password: '',
        clientId: '',
        clientSecret: '',
        tokenUrl: '',
        apiKey: '',
        syncIntervalMinutes: 15,
        serviceUrl: '',
        odataVersion: 'V2',
        extraConfig: { syncCompanies: true, syncContacts: true, syncDeals: true },
      });
    } else {
      setForm({
        type: v as IntegrationType,
        name: form.name,
        baseUrl: '',
        authType: 'BASIC',
        username: '',
        password: '',
        clientId: '',
        clientSecret: '',
        tokenUrl: '',
        apiKey: '',
        syncIntervalMinutes: 60,
        serviceUrl: '',
        odataVersion: 'V2',
        extraConfig: {},
      });
    }
  };

  const isHubspot = form.type === 'HUBSPOT';
  const isSap = !isHubspot;

  const handleSubmit = async () => {
    if (!form.name) {
      toast({ title: 'El nombre es requerido', variant: 'destructive' });
      return;
    }
    if (isSap && !form.baseUrl) {
      toast({ title: 'Nombre y URL base son requeridos', variant: 'destructive' });
      return;
    }
    if (isHubspot && !form.apiKey && !editConfig) {
      toast({ title: 'El token de la Private App es requerido', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      // Build payload — omit SAP-specific fields for HubSpot
      const payload: CreateIntegrationPayload = isHubspot
        ? {
            type: form.type,
            name: form.name,
            authType: 'API_KEY',
            apiKey: form.apiKey || undefined,
            syncIntervalMinutes: form.syncIntervalMinutes,
            extraConfig: form.extraConfig,
          }
        : {
            ...form,
            tokenUrl: form.tokenUrl || undefined,
            serviceUrl: form.serviceUrl || undefined,
            username: form.username || undefined,
            password: form.password || undefined,
            clientId: form.clientId || undefined,
            clientSecret: form.clientSecret || undefined,
            apiKey: form.apiKey || undefined,
          };

      if (editConfig) {
        await updateConfig(editConfig.id, payload);
        toast({ title: 'Integración actualizada', variant: 'success' });
      } else {
        await createConfig(payload);
        toast({ title: 'Integración creada', variant: 'success' });
      }
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Error al guardar',
        description: error?.response?.data?.error || error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editConfig ? 'Editar Integración' : 'Nueva Integración'}</DialogTitle>
          <DialogDescription>
            Configura una conexión a SAP S/4HANA, SAP C4C o HubSpot CRM.
          </DialogDescription>
        </DialogHeader>

        {/* Panel explicativo de prerequisitos */}
        {form.type === 'SAP_S4HANA' && (
          <details className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-4 text-sm">
            <summary className="cursor-pointer font-medium text-blue-900 dark:text-blue-100">
              ℹ️ Qué tenés que tener configurado en SAP antes de empezar
            </summary>
            <div className="mt-3 space-y-3 text-blue-900/90 dark:text-blue-100/90">
              <div>
                <p className="font-semibold mb-1">1. URL del servicio OData (anchor)</p>
                <p>
                  La integración asume <strong>SAP S/4HANA Cloud Public</strong> con servicios
                  estándar de la API Business Hub. Configurá el <em>Service URL</em> apuntando a:
                </p>
                <ul className="list-disc list-inside mt-1 ml-2 space-y-0.5">
                  <li>
                    <strong>Sandbox:</strong>{' '}
                    <code className="text-xs">
                      https://sandbox.api.sap.com/s4hanacloud/sap/opu/odata/sap/API_PRODUCT_SRV
                    </code>
                  </li>
                  <li>
                    <strong>Productivo:</strong>{' '}
                    <code className="text-xs">
                      https://&lt;tenant&gt;-api.s4hana.cloud.sap/sap/opu/odata/sap/API_PRODUCT_SRV
                    </code>
                  </li>
                </ul>
                <p className="mt-1 text-xs">
                  Los otros syncs (Países, LOB / Segment, Profit Centers) se derivan automáticamente
                  reemplazando el segmento final por <code>API_COUNTRY_SRV</code>,{' '}
                  <code>API_SEGMENT_SRV</code>, <code>API_PROFITCENTER_SRV</code>.
                </p>
              </div>

              <div>
                <p className="font-semibold mb-1">2. Autenticación</p>
                <ul className="list-disc list-inside ml-2 space-y-0.5">
                  <li>
                    <strong>Sandbox:</strong> API Key (header <code>APIKey</code>), generada en{' '}
                    <a
                      href="https://api.sap.com"
                      target="_blank"
                      rel="noreferrer"
                      className="underline"
                    >
                      api.sap.com
                    </a>{' '}
                    → "Show API Key".
                  </li>
                  <li>
                    <strong>Productivo:</strong> OAuth 2.0 Client Credentials. Pedirle al admin SAP
                    que active un <strong>Communication Arrangement</strong> con scenario{' '}
                    <code>SAP_COM_0009</code> (Product Integration) o equivalente según el rol.
                  </li>
                </ul>
              </div>

              <div>
                <p className="font-semibold mb-1">
                  3. Entitlements del API Key / Communication Scenario
                </p>
                <p>
                  Verificá que la API Key (o el rol del usuario OAuth) tenga acceso a los servicios
                  que vas a sincronizar. Si recibís <strong>403 Forbidden</strong> al testear, falta
                  entitlement para ese servicio en particular.
                </p>
              </div>

              <div>
                <p className="font-semibold mb-1">4. Activá los sub-syncs que necesites</p>
                <p>
                  En <em>"Configuración avanzada"</em> abajo, prendé los toggles de{' '}
                  <code>syncCountries</code>, <code>syncLobs</code>, <code>syncProfitCenters</code>{' '}
                  según corresponda. Materiales siempre se sincroniza si la integración está activa.
                </p>
              </div>

              <div className="text-xs italic">
                Para SAP customizado (on-prem, OData V4 puro, nombres de servicio no estándar),
                contactar al equipo de soporte — esta integración está optimizada para S/4HANA Cloud
                Public estándar.
              </div>
            </div>
          </details>
        )}

        {form.type === 'SAP_C4C' && (
          <details className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-4 text-sm">
            <summary className="cursor-pointer font-medium text-blue-900 dark:text-blue-100">
              ℹ️ Qué tenés que tener configurado en SAP C4C
            </summary>
            <div className="mt-3 space-y-2 text-blue-900/90 dark:text-blue-100/90">
              <p>
                <strong>Service URL:</strong>{' '}
                <code className="text-xs">
                  https://&lt;tenant&gt;.crm.ondemand.com/sap/c4c/odata/v1/c4codataapi
                </code>
              </p>
              <p>
                <strong>Auth:</strong> BASIC (usuario/password de un Communication User en C4C) u
                OAuth 2.0 según el setup de tu tenant.
              </p>
              <p className="text-xs">Se sincronizan oportunidades inbound desde C4C → Prixia.</p>
            </div>
          </details>
        )}

        <div className="space-y-4 py-4">
          {/* Tipo */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo de integración</Label>
              <SearchableSelect
                options={TYPE_OPTIONS}
                value={form.type}
                onValueChange={handleTypeChange}
                placeholder="Seleccionar tipo"
              />
            </div>
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej: SAP Producción"
              />
            </div>
          </div>

          {/* HubSpot-specific fields */}
          {isHubspot && (
            <>
              <div className="space-y-2">
                <Label>Private App Token</Label>
                <Input
                  type="password"
                  value={form.apiKey || ''}
                  onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                  placeholder={
                    editConfig ? '--------' : 'pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx'
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Generá una Private App en HubSpot con scopes crm.objects.contacts.read,
                  crm.objects.companies.read, crm.objects.deals.read.
                </p>
              </div>

              <div className="space-y-2">
                <Label>Intervalo de sync (minutos)</Label>
                <Input
                  type="number"
                  min={5}
                  max={1440}
                  value={form.syncIntervalMinutes}
                  onChange={(e) =>
                    setForm({ ...form, syncIntervalMinutes: parseInt(e.target.value) || 15 })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Cada cuántos minutos se sincroniza con HubSpot. Mín. 5 min · Máx. 1440 min (24
                  hs).
                </p>
              </div>

              <details className="rounded-lg border border-border bg-muted/30 p-4">
                <summary className="cursor-pointer text-sm font-medium">
                  Objetos a sincronizar
                </summary>
                <div className="mt-4">
                  <ExtraConfigEditor
                    fields={HUBSPOT_EXTRA_FIELDS}
                    value={form.extraConfig}
                    onChange={(next) => setForm({ ...form, extraConfig: next })}
                  />
                </div>
              </details>
            </>
          )}

          {/* SAP-specific fields */}
          {isSap && (
            <>
              {/* URLs — onChange limpia whitespace inmediatamente para que el usuario vea la URL final */}
              <div className="space-y-2">
                <Label>URL Base</Label>
                <Input
                  value={form.baseUrl}
                  onChange={(e) =>
                    setForm({ ...form, baseUrl: e.target.value.replace(/\s+/g, '') })
                  }
                  placeholder="https://my-sap-system.example.com/sap/opu/odata/sap"
                />
              </div>
              <div className="space-y-2">
                <Label>URL del Servicio OData (opcional)</Label>
                <Input
                  value={form.serviceUrl || ''}
                  onChange={(e) =>
                    setForm({ ...form, serviceUrl: e.target.value.replace(/\s+/g, '') })
                  }
                  placeholder="https://my-sap-system.example.com/sap/opu/odata/sap/API_PRODUCT_SRV"
                />
              </div>

              {/* OData + Sync */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Versión OData</Label>
                  <SearchableSelect
                    options={ODATA_OPTIONS}
                    value={form.odataVersion || 'V2'}
                    onValueChange={(v) => setForm({ ...form, odataVersion: v })}
                    placeholder="Seleccionar versión"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Intervalo de sync (minutos)</Label>
                  <Input
                    type="number"
                    min={5}
                    max={1440}
                    value={form.syncIntervalMinutes}
                    onChange={(e) =>
                      setForm({ ...form, syncIntervalMinutes: parseInt(e.target.value) || 60 })
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    Cada cuántos minutos se sincroniza con SAP. El worker chequea cada 5 min y
                    dispara la sync si pasó este intervalo. Mín. 5 min · Máx. 1440 min (24 hs).
                  </p>
                </div>
              </div>

              {/* Auth */}
              <div className="space-y-2">
                <Label>Tipo de autenticación</Label>
                <SearchableSelect
                  options={AUTH_OPTIONS}
                  value={form.authType}
                  onValueChange={(v) => setForm({ ...form, authType: v as IntegrationAuthType })}
                  placeholder="Seleccionar autenticación"
                />
              </div>

              {form.authType === 'BASIC' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Usuario</Label>
                    <Input
                      value={form.username || ''}
                      onChange={(e) => setForm({ ...form, username: e.target.value })}
                      placeholder="SAP_USER"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Contraseña</Label>
                    <Input
                      type="password"
                      value={form.password || ''}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      placeholder={editConfig ? '--------' : 'Contraseña'}
                    />
                  </div>
                </div>
              )}

              {form.authType === 'OAUTH2' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Client ID</Label>
                      <Input
                        value={form.clientId || ''}
                        onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Client Secret</Label>
                      <Input
                        type="password"
                        value={form.clientSecret || ''}
                        onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
                        placeholder={editConfig ? '--------' : ''}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Token URL</Label>
                    <Input
                      value={form.tokenUrl || ''}
                      onChange={(e) =>
                        setForm({ ...form, tokenUrl: e.target.value.replace(/\s+/g, '') })
                      }
                      placeholder="https://oauth.sap.com/oauth/token"
                    />
                  </div>
                </div>
              )}

              {form.authType === 'API_KEY' && (
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={form.apiKey || ''}
                    onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                    placeholder={editConfig ? '--------' : 'API Key'}
                  />
                </div>
              )}

              {form.type === 'SAP_S4HANA' && (
                <details className="rounded-lg border border-border bg-muted/30 p-4">
                  <summary className="cursor-pointer text-sm font-medium">
                    Configuración avanzada (parametrizable)
                  </summary>
                  <div className="mt-4">
                    <ExtraConfigEditor
                      fields={S4HANA_EXTRA_FIELDS}
                      value={form.extraConfig}
                      onChange={(next) => setForm({ ...form, extraConfig: next })}
                    />
                  </div>
                </details>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Guardando...' : editConfig ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
